import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { APIErrorResponse } from './_types'
import { DataAssetType } from './use-data-assets-query'

const CREATE_DATA_ASSET_ERROR = 'FAILED_TO_CREATE_DATA_ASSET'

type CreateDataAssetRequest = {
  dataType: DataAssetType
  files: File[]
  fileDescriptions: string
}

type CreateDataAssetAPIResponse = {
  success: boolean
  message?: string
  data?: any
}

function useCreateDataAssetMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['create-data-asset'],
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['data-assets', domainId] })
    },
    mutationFn: async ({
      dataType,
      files,
      fileDescriptions,
    }: CreateDataAssetRequest): Promise<CreateDataAssetAPIResponse> => {
      const formData = new FormData()

      if (files && files.length > 0) {
        for (const file of files) {
          formData.append('files', file)
        }
      }

      formData.append('file_descriptions', fileDescriptions)

      const response = await apiRequest({
        path: taskManagerApiPaths.files({ domainId }),
        queryParams: {
          data_type: dataType,
        },
        type: 'MULTIPART',
        options: {
          method: 'POST',
          body: formData,
        },
      })

      if (!response.ok) {
        if (response.status === 503) {
          throw new Error('Service unavailable. Please try again.')
        }

        const errorData = (await response.json()) as APIErrorResponse

        if (errorData?.detail) {
          throw new Error(errorData.detail)
        }

        throw new Error(CREATE_DATA_ASSET_ERROR)
      }

      const data = (await response.json()) as CreateDataAssetAPIResponse
      return data
    },
  })
}

export { CREATE_DATA_ASSET_ERROR, useCreateDataAssetMutation }
